/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   Bundle bizos
 */

import { Bizo, autoclass } from './base'
import type { BuildingListingCollection, Bundle as BundleType, RawBundle } from './bundle.types'
import { User } from './user'
import type { NonLocalUser } from './user.types'

const DEFAULT_BUILDING_LISTING_COLLECTION = { buildings: {}, listings: {} }

const isBuildingListingCollection = (
  data: Partial<BuildingListingCollection>
): data is BuildingListingCollection => !!data.buildings && !!data.listings

const buildingsListingsWithDefaults = (
  data: Partial<BuildingListingCollection>
): BuildingListingCollection =>
  isBuildingListingCollection(data)
    ? data
    : {
        buildings: data.buildings ?? {},
        listings: data.listings ?? {},
      }

const buildingListingCollectionGetter =
  <
    Key extends NonNullable<
      {
        [Key in keyof RawBundle]: RawBundle[Key] extends BuildingListingCollection | undefined
          ? Key
          : never
      }[keyof RawBundle]
    >,
  >(
    field: Key
  ) =>
  (bundle: Bundle) => {
    const value = bundle._data[field]
    return value ? buildingsListingsWithDefaults(value) : DEFAULT_BUILDING_LISTING_COLLECTION
  }

export const Bundle: typeof BundleType = autoclass<Bundle, RawBundle, Bizo<Bundle, RawBundle>>(
  'Bundle',
  class Bundle extends Bizo<Bundle, RawBundle> {},
  {
    alerts: (bundle: Bundle) => bundle._data.alerts ?? 0,
    csrf: 'csrf',
    dislike: buildingListingCollectionGetter('dislike'),
    equalApplyMessaged: buildingListingCollectionGetter('equalApplyMessaged'),
    equalCheckAvailabilityMessaged: buildingListingCollectionGetter(
      'equalCheckAvailabilityMessaged'
    ),
    equalTourMessaged: buildingListingCollectionGetter('equalTourMessaged'),
    favorites: buildingListingCollectionGetter('favorites'),
    messaged: buildingListingCollectionGetter('messaged'),
    notices: (bundle: Bundle) => bundle._data.notices ?? 0,
    redirectTo: (bundle: Bundle) => bundle._data.redirect_to ?? null,
    user: (bundle: Bundle) => (User.fromJS(bundle._data.user) as unknown as NonLocalUser) ?? null,
    visited: (bundle: Bundle) => bundle._data.visited || {},
    xzToken: 'xz_token',
    sudoer: 'sudoer',
  }
)

export type Bundle = BundleType
